import { useState } from 'react'
import { recapApi } from '../api'
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react'

function getMonthLabel(month) {
  const [y, m] = month.split('-')
  return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
}

export default function Recap() {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const init = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`

  const [month, setMonth] = useState(init)
  const [recap, setRecap] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number)
    const d = new Date(y, m - 2, 1)
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    setRecap(null)
  }

  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number)
    const d = new Date(y, m, 1)
    const now = new Date()
    if (d > now) return
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    setRecap(null)
  }

  const isCurrentMonth = month === init

  const generate = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await recapApi.get(month)
      setRecap(res.recap)
      setData(res.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="px-4 w-full max-w-lg mx-auto" style={{ paddingTop: '24px', paddingBottom: '120px' }}>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text)' }}>Bilan mensuel</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Résumé généré par IA depuis tes données</p>
      </div>

      {/* Sélecteur de mois */}
      <div className="glass rounded-3xl p-3 flex items-center justify-between mb-5">
        <button onClick={prevMonth} className="p-2 rounded-2xl" style={{ background: 'var(--bg3)' }}>
          <ChevronLeft size={16} style={{ color: 'var(--muted)' }} />
        </button>
        <p className="text-sm font-semibold capitalize" style={{ color: 'var(--text)' }}>
          {getMonthLabel(month)}
        </p>
        <button onClick={nextMonth} disabled={isCurrentMonth}
          className="p-2 rounded-2xl"
          style={{ background: 'var(--bg3)', opacity: isCurrentMonth ? 0.3 : 1 }}>
          <ChevronRight size={16} style={{ color: 'var(--muted)' }} />
        </button>
      </div>

      {/* Stats rapides si données chargées */}
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Blocs faits', value: data.blocks?.done ?? 0, color: 'var(--accent)' },
            { label: 'Streak max', value: `🔥 ${data.maxStreak}`, color: 'var(--done)' },
            { label: 'Livres finis', value: data.booksFinished?.length ?? 0, color: 'var(--dot-orange)' },
          ].map(s => (
            <div key={s.label} className="glass-inner rounded-2xl py-3 flex flex-col items-center gap-0.5">
              <span className="text-lg font-bold" style={{ color: s.color }}>{s.value}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-center"
                style={{ color: 'var(--muted)' }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bouton générer */}
      {!recap && (
        <button onClick={generate} disabled={loading}
          className="w-full py-4 rounded-3xl text-sm font-semibold flex items-center justify-center gap-2 transition"
          style={{
            background: loading ? 'var(--bg2)' : 'var(--accent)',
            color: loading ? 'var(--muted)' : 'white',
            boxShadow: loading ? 'none' : '0 4px 20px rgba(123,111,208,0.35)',
          }}>
          {loading ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 animate-spin"
                style={{ borderColor: 'rgba(123,111,208,0.3)', borderTopColor: 'var(--accent)' }} />
              Claude analyse tes données…
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Générer le bilan de {getMonthLabel(month)}
            </>
          )}
        </button>
      )}

      {/* Erreur */}
      {error && (
        <div className="rounded-2xl px-4 py-3 mt-4 text-sm"
          style={{ background: 'var(--skip-bg)', color: 'var(--skip)' }}>
          {error}
        </div>
      )}

      {/* Résumé */}
      {recap && (
        <div className="glass rounded-3xl p-5 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={14} style={{ color: 'var(--accent)' }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
              Bilan · {getMonthLabel(month)}
            </p>
          </div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text2)' }}>
            {recap}
          </div>
          <button onClick={() => setRecap(null)}
            className="mt-5 w-full py-2.5 rounded-2xl text-xs font-semibold"
            style={{ background: 'var(--bg3)', color: 'var(--muted)' }}>
            Regénérer
          </button>
        </div>
      )}
    </div>
  )
}
